import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  api,
  type AdminSettings,
  type AdminUser,
  type ApiKeyCreated,
  type ApiKeyScope,
  type ApiKeySummary,
  type FravikBands,
  type VaktAuth,
  type VaktTestResult,
} from "../lib/api";
import { isAdmin, useAuth } from "../lib/auth";
import { FravikBandEditor } from "../components/maelabord/FravikBandEditor";
import { bandsEqual } from "../components/maelabord/fravikBands";

export const Route = createFileRoute("/admin/stillingar")({
  component: AdminSettingsPage,
});

const card = "rounded-2xl border border-line bg-white p-5 shadow-sm";
const input =
  "mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
const label = "block text-[12.5px] font-semibold text-ink";

const VAKT_FIELD_LABELS: Record<string, string> = {
  supplyTempC: "Framrás (°C)",
  returnTempC: "Bakrás (°C)",
  commandedPositionPct: "Lokastaða — stýrt (%)",
  actualPositionPct: "Lokastaða — mæld (%)",
  flowLps: "Flæði (L/s)",
  heatKw: "Afl (kW)",
};

interface SmtpForm {
  host: string;
  port: number;
  useSsl: boolean;
  user: string;
  password: string;
  from: string;
  webBaseUrl: string;
}

interface VaktForm {
  baseUrl: string;
  auth: VaktAuth;
  username: string;
  password: string;
  token: string;
}

function swapSupplyReturn(map: Record<string, string>): Record<string, string> {
  return {
    ...map,
    supplyTempC: map.returnTempC ?? "",
    returnTempC: map.supplyTempC ?? "",
  };
}

function temp(c: number | null): string {
  return c === null
    ? "–"
    : `${c.toLocaleString("is-IS", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} °C`;
}

interface LaufidForm {
  partnerSlug: string;
  baseUrl: string;
  appUrl: string;
  token: string;
  tokenSet: boolean;
}

function AdminSettingsPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [recipients, setRecipients] = useState<Set<string>>(new Set());
  const [rollingWindow, setRollingWindow] = useState(6);
  const [tariffPerM3, setTariffPerM3] = useState(220);
  const [bands, setBands] = useState<FravikBands | null>(null);
  const [savedBands, setSavedBands] = useState<FravikBands | null>(null);
  const [smtp, setSmtp] = useState<SmtpForm>({
    host: "",
    port: 587,
    useSsl: true,
    user: "",
    password: "",
    from: "",
    webBaseUrl: "",
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [vakt, setVakt] = useState<VaktForm>({
    baseUrl: "",
    auth: "login",
    username: "",
    password: "",
    token: "",
  });
  const [vaktPasswordSet, setVaktPasswordSet] = useState(false);
  const [vaktTokenSet, setVaktTokenSet] = useState(false);
  const [laufid, setLaufid] = useState<LaufidForm>({
    partnerSlug: "",
    baseUrl: "",
    appUrl: "",
    token: "",
    tokenSet: false,
  });
  const [keyMap, setKeyMap] = useState<Record<string, string>>({});
  const [vaktTest, setVaktTest] = useState<VaktTestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const apply = useCallback((s: AdminSettings) => {
    setRecipients(new Set(s.recipientUserIds));
    setRollingWindow(s.rollingWindow);
    setTariffPerM3(s.tariffPerM3);
    setBands(s.bands);
    setSavedBands(s.bands);
    setPasswordSet(s.smtp.passwordSet);
    setSmtp({
      host: s.smtp.host ?? "",
      port: s.smtp.port,
      useSsl: s.smtp.useSsl,
      user: s.smtp.user ?? "",
      password: "",
      from: s.smtp.from ?? "",
      webBaseUrl: s.smtp.webBaseUrl ?? "",
    });
    setVaktPasswordSet(s.vakt.passwordSet);
    setVaktTokenSet(s.vakt.tokenSet);
    setVakt({
      baseUrl: s.vakt.baseUrl ?? "",
      auth: s.vakt.auth,
      username: s.vakt.username ?? "",
      password: "",
      token: "",
    });
    setKeyMap({ ...s.vakt.keyMap });
    setLaufid({
      partnerSlug: s.laufid.partnerSlug,
      baseUrl: s.laufid.baseUrl,
      appUrl: s.laufid.appUrl,
      token: "",
      tokenSet: s.laufid.tokenSet,
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settings, userList] = await Promise.all([
        api.admin.getSettings(),
        api.admin.listUsers(),
      ]);
      apply(settings);
      setUsers(userList.users);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isAdmin(me)) {
    return (
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        <h1 className="text-xl font-bold text-ink">Enginn aðgangur</h1>
        <p className="mt-2 text-sm text-muted-2">
          Aðeins stjórnendur hafa aðgang að stillingum.
        </p>
      </div>
    );
  }

  function toggleRecipient(id: string) {
    setRecipients((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save() {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const result = await api.admin.updateSettings({
        recipientUserIds: [...recipients],
        rollingWindow,
        tariffPerM3,
        ...(bands ? { bands } : {}),
        smtp: {
          host: smtp.host,
          port: smtp.port,
          useSsl: smtp.useSsl,
          user: smtp.user,
          from: smtp.from,
          webBaseUrl: smtp.webBaseUrl,
          ...(smtp.password ? { password: smtp.password } : {}),
        },
        vakt: {
          baseUrl: vakt.baseUrl,
          auth: vakt.auth,
          username: vakt.username,
          keyMap,
          ...(vakt.password ? { password: vakt.password } : {}),
          ...(vakt.token ? { token: vakt.token } : {}),
        },
        laufid: {
          partnerSlug: laufid.partnerSlug,
          baseUrl: laufid.baseUrl,
          appUrl: laufid.appUrl,
          ...(laufid.token ? { token: laufid.token } : {}),
        },
      });
      apply(result);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function testVakt() {
    setTesting(true);
    setVaktTest(null);
    try {
      setVaktTest(
        await api.admin.testVakt({
          baseUrl: vakt.baseUrl || undefined,
          auth: vakt.auth,
          username: vakt.username || undefined,
          keyMap,
          ...(vakt.password ? { password: vakt.password } : {}),
          ...(vakt.token ? { token: vakt.token } : {}),
        }),
      );
    } catch (err) {
      setVaktTest({ ok: false, error: (err as Error).message });
    } finally {
      setTesting(false);
    }
  }

  const vaktConnected =
    vakt.auth === "token"
      ? Boolean(vakt.baseUrl && vaktTokenSet)
      : Boolean(vakt.baseUrl && vakt.username && vaktPasswordSet);
  const flowing = vaktTest?.orientation?.filter((o) => o.samples > 0) ?? [];
  const inverted = flowing.filter((o) => o.inverted);

  return (
    <div className="mx-auto max-w-[900px] px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold text-ink">Stillingar</h1>
      <p className="mt-1 text-sm text-muted-2">
        Frávikstilkynningar, vaktarafrit, næmni greiningar og tölvupóstur.
      </p>

      {loading ? (
        <p className="mt-6 text-sm text-muted-3">Hleð…</p>
      ) : (
        <div className="mt-6 space-y-6">
          <section className={card}>
            <h2 className="text-base font-bold text-ink">
              Afrit / enginn á vakt
            </h2>
            <p className="mt-1 text-[12.5px] text-muted-2">
              Fá tölvupóst um frávik þegar enginn er skráður á vakt í Vaktaskrá.
              Tengiliðir viðskiptavina fá einnig póst ef þeir eru merktir á
              fyrirtækinu.
            </p>
            <div className="mt-3 divide-y divide-line">
              {users.map((u) => (
                <label
                  key={u.id}
                  className="flex cursor-pointer items-center gap-3 py-2.5 first:pt-0"
                >
                  <input
                    type="checkbox"
                    checked={recipients.has(u.id)}
                    onChange={() => toggleRecipient(u.id)}
                    className="h-4 w-4 accent-brand"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">
                      {u.displayName || u.email}
                    </span>
                    <span className="block truncate text-[11.5px] text-muted-3">
                      {u.email}
                      {!u.isActive && " · óvirkur"}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section className={card}>
            <h2 className="text-base font-bold text-ink">Næmni greiningar</h2>
            <label className="mt-3 block">
              <span className={label}>
                Rúllugluggi (fjöldi nýjustu mælinga)
              </span>
              <input
                type="number"
                min={1}
                value={rollingWindow}
                onChange={(e) =>
                  setRollingWindow(Math.max(1, Number(e.target.value) || 1))
                }
                className={`${input} max-w-[160px]`}
              />
            </label>
            <p className="mt-1.5 text-[11.5px] text-muted-3">
              Hita- og opnunarmörk eru metin út frá meðaltali síðustu{" "}
              {rollingWindow} mælinga. Rennsli og rúmmál eru borin saman við
              meðaltal síðustu 7 daga.
            </p>

            {bands && (
              <div className="mt-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={label}>Frávikamörk (sjálfgefin)</span>
                  <button
                    type="button"
                    disabled={!savedBands || bandsEqual(bands, savedBands)}
                    onClick={() => savedBands && setBands(savedBands)}
                    className="rounded-md border border-line px-2 py-1 text-[11.5px] font-medium text-muted-2 hover:bg-canvas disabled:opacity-40"
                  >
                    Afturkalla breytingar
                  </button>
                </div>
                <div className="mt-2">
                  <FravikBandEditor bands={bands} onChange={setBands} />
                </div>
                <p className="mt-1.5 text-[11.5px] text-muted-3">
                  Gildir um alla loka sem hafa ekki sérsniðin mörk. Stilltu
                  einstaka loka í Mælaborð → Tæki.
                </p>

                <div className="mt-3 flex flex-wrap gap-4">
                  <label className="block">
                    <span className={label}>Rennsli — gult (× meðaltal)</span>
                    <input
                      type="number"
                      min={1}
                      step={0.05}
                      value={bands.flowYellowFrac}
                      onChange={(e) =>
                        setBands({
                          ...bands,
                          flowYellowFrac: Number(e.target.value) || 1,
                        })
                      }
                      className={`${input} max-w-[140px]`}
                    />
                  </label>
                  <label className="block">
                    <span className={label}>Rennsli — rautt (× meðaltal)</span>
                    <input
                      type="number"
                      min={1}
                      step={0.05}
                      value={bands.flowRedFrac}
                      onChange={(e) =>
                        setBands({
                          ...bands,
                          flowRedFrac: Number(e.target.value) || 1,
                        })
                      }
                      className={`${input} max-w-[140px]`}
                    />
                  </label>
                </div>
              </div>
            )}

            <label className="mt-4 block">
              <span className={label}>Gjaldskrá heitavatns (kr./m³)</span>
              <input
                type="number"
                min={0}
                step={1}
                value={tariffPerM3}
                onChange={(e) =>
                  setTariffPerM3(Math.max(0, Number(e.target.value) || 0))
                }
                className={`${input} max-w-[160px]`}
              />
            </label>
            <p className="mt-1.5 text-[11.5px] text-muted-3">
              Notað til að reikna áætlaðan sparnað í Hitakerfi (m³ × gjaldskrá).
            </p>
          </section>

          <section className={card}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-ink">
                Tölvupóstur (SMTP)
              </h2>
              {passwordSet && (
                <span className="rounded-full bg-ok-50 px-2 py-0.5 text-[11px] font-semibold text-ok">
                  lykilorð sett
                </span>
              )}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label>
                <span className={label}>Hýsill</span>
                <input
                  value={smtp.host}
                  onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
                  className={input}
                  placeholder="smtp.dæmi.is"
                />
              </label>
              <label>
                <span className={label}>Port</span>
                <input
                  type="number"
                  value={smtp.port}
                  onChange={(e) =>
                    setSmtp({ ...smtp, port: Number(e.target.value) || 587 })
                  }
                  className={input}
                />
              </label>
              <label>
                <span className={label}>Notandi</span>
                <input
                  value={smtp.user}
                  onChange={(e) => setSmtp({ ...smtp, user: e.target.value })}
                  className={input}
                />
              </label>
              <label>
                <span className={label}>
                  Lykilorð {passwordSet && "(tómt = óbreytt)"}
                </span>
                <input
                  type="password"
                  value={smtp.password}
                  onChange={(e) =>
                    setSmtp({ ...smtp, password: e.target.value })
                  }
                  className={input}
                  placeholder={passwordSet ? "••••••••" : ""}
                />
              </label>
              <label>
                <span className={label}>Sendandi (from)</span>
                <input
                  value={smtp.from}
                  onChange={(e) => setSmtp({ ...smtp, from: e.target.value })}
                  className={input}
                  placeholder="fravik@dæmi.is"
                />
              </label>
              <label>
                <span className={label}>Grunnslóð vefs (webBaseUrl)</span>
                <input
                  value={smtp.webBaseUrl}
                  onChange={(e) =>
                    setSmtp({ ...smtp, webBaseUrl: e.target.value })
                  }
                  className={input}
                  placeholder="https://atomika.is"
                />
              </label>
            </div>
            <label className="mt-3 flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={smtp.useSsl}
                onChange={(e) => setSmtp({ ...smtp, useSsl: e.target.checked })}
                className="h-4 w-4 accent-brand"
              />
              <span className="text-[12.5px] font-semibold text-ink">
                Nota SSL/TLS
              </span>
            </label>
          </section>

          <section className={card}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-ink">
                vakt (gagnaveita)
              </h2>
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                  vaktConnected
                    ? "bg-ok-50 text-ok"
                    : "bg-slate-100 text-muted-2"
                }`}
              >
                {vaktConnected ? "tengt" : "ekki tengt"}
              </span>
            </div>
            <p className="mt-1 text-[12.5px] text-muted-2">
              Lifandi mælingar sóttar úr gagnaveitu með ThingsBoard-viðmóti.
              vakt víxlar fram- og bakrás og vörpunin að neðan leiðréttir það.
              Gagnaveita sem merkir rétt þarf öfuga vörpun: breyttu slóð,
              auðkenningu og vörpun í sömu vistun og prófaðu tenginguna fyrst.
            </p>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label>
                <span className={label}>Grunnslóð</span>
                <input
                  value={vakt.baseUrl}
                  onChange={(e) =>
                    setVakt({ ...vakt, baseUrl: e.target.value })
                  }
                  className={input}
                  placeholder="https://vakt.systemat.is"
                />
              </label>
              <label>
                <span className={label}>Auðkenning</span>
                <select
                  value={vakt.auth}
                  onChange={(e) =>
                    setVakt({ ...vakt, auth: e.target.value as VaktAuth })
                  }
                  className={input}
                >
                  <option value="login">Notandi og lykilorð</option>
                  <option value="token">Aðgangslykill (token)</option>
                </select>
              </label>
              {vakt.auth === "login" ? (
                <>
                  <label>
                    <span className={label}>Notandi (netfang)</span>
                    <input
                      value={vakt.username}
                      onChange={(e) =>
                        setVakt({ ...vakt, username: e.target.value })
                      }
                      className={input}
                    />
                  </label>
                  <label>
                    <span className={label}>
                      Lykilorð {vaktPasswordSet && "(tómt = óbreytt)"}
                    </span>
                    <input
                      type="password"
                      value={vakt.password}
                      onChange={(e) =>
                        setVakt({ ...vakt, password: e.target.value })
                      }
                      className={input}
                      placeholder={vaktPasswordSet ? "••••••••" : ""}
                    />
                  </label>
                </>
              ) : (
                <label>
                  <span className={label}>
                    Aðgangslykill {vaktTokenSet && "(tómt = óbreytt)"}
                  </span>
                  <input
                    type="password"
                    value={vakt.token}
                    onChange={(e) =>
                      setVakt({ ...vakt, token: e.target.value })
                    }
                    className={input}
                    placeholder={vaktTokenSet ? "••••••••" : "atk_…"}
                    autoComplete="off"
                  />
                </label>
              )}
            </div>

            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => void testVakt()}
                disabled={testing}
                className="rounded-lg border border-line px-3 py-2 text-[12.5px] font-bold text-ink disabled:opacity-50"
              >
                {testing ? "Prófa…" : "Prófa tengingu"}
              </button>
              {vaktTest && !vaktTest.ok && (
                <span className="text-[12.5px] font-semibold text-danger">
                  {vaktTest.error}
                </span>
              )}
              {vaktTest?.ok && (
                <span className="text-[12.5px] font-semibold text-ok">
                  {vaktTest.deviceCount} tæki fundust
                </span>
              )}
            </div>

            {vaktTest?.ok && (
              <div className="mt-3 rounded-xl border border-line bg-canvas p-3">
                {vaktTest.sampleKeys && vaktTest.sampleKeys.length > 0 && (
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-4">
                      Lyklar ({vaktTest.sampleDevice})
                    </div>
                    <div className="mt-1 max-h-24 overflow-auto text-[11.5px] text-muted-2">
                      {vaktTest.sampleKeys.join(", ")}
                    </div>
                  </div>
                )}
                {vaktTest.orientation && (
                  <div className="mt-3">
                    <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-4">
                      Framrás og bakrás (síðasti sólarhringur, með flæði)
                    </div>
                    <p
                      className={`mt-1 text-[12.5px] font-semibold ${
                        inverted.length > 0 ? "text-danger" : "text-ok"
                      }`}
                    >
                      Framrás heitari en bakrás í{" "}
                      {flowing.length - inverted.length} af {flowing.length}{" "}
                      lokum með flæði.
                      {inverted.length > 0 &&
                        " Ef það á við flestar lokur er vörpunin öfug — ekki vista fyrr en það er leiðrétt."}
                    </p>
                    <ul className="mt-1 max-h-40 overflow-auto text-[11.5px] text-muted-2">
                      {vaktTest.orientation.map((o) => (
                        <li
                          key={`${o.site}/${o.valve}`}
                          className={
                            o.inverted ? "font-semibold text-danger" : ""
                          }
                        >
                          {o.site} {o.valve}:{" "}
                          {o.samples === 0
                            ? "ekkert flæði"
                            : `framrás ${temp(o.supplyC)} · bakrás ${temp(o.returnC)}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="mt-2 text-[12px] text-muted-3">
                  Tengdu byggingar við viðskiptavini í Mælaborð → Tæki.
                </p>
              </div>
            )}

            <div className="mt-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-4">
                  Vörpun mæligilda (okkar reitur → vakt lykill)
                </div>
                <button
                  type="button"
                  onClick={() => setKeyMap(swapSupplyReturn(keyMap))}
                  className="rounded-lg border border-line px-2.5 py-1 text-[12px] font-bold text-ink"
                >
                  Víxla fram- og bakrás
                </button>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {Object.keys(keyMap).map((field) => (
                  <label key={field} className="flex items-center gap-2">
                    <span className="w-40 flex-none text-[12px] font-semibold text-ink">
                      {VAKT_FIELD_LABELS[field] ?? field}
                    </span>
                    <input
                      value={keyMap[field]}
                      onChange={(e) =>
                        setKeyMap({ ...keyMap, [field]: e.target.value })
                      }
                      className="flex-1 rounded-lg border border-line px-2.5 py-1.5 text-[12.5px] outline-none focus:border-brand"
                    />
                  </label>
                ))}
              </div>
            </div>
          </section>

          <section className={card}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-ink">
                Laufið (stofnun viðskiptavina)
              </h2>
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                  laufid.tokenSet
                    ? "bg-ok-50 text-ok"
                    : "bg-slate-100 text-muted-2"
                }`}
              >
                {laufid.tokenSet ? "uppsett" : "ekki uppsett"}
              </span>
            </div>
            <p className="mt-1 text-[12.5px] text-muted-2">
              Partner-lykill sem Markaðurinn notar til að stofna fyrirtæki í
              Laufinu. Laufið gefur lykilinn út og hann auðkennir Atomika sem
              kerfi — engin innskráning, ekkert lykilorð, enginn notandi sem
              hægt er að reka út. Stofnun viðskiptavinar er óafturkræf og aðeins
              stjórnendum heimil.
            </p>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label>
                <span className={label}>Grunnslóð (API)</span>
                <input
                  value={laufid.baseUrl}
                  onChange={(e) =>
                    setLaufid({ ...laufid, baseUrl: e.target.value })
                  }
                  className={input}
                  placeholder="https://api.laufid.is"
                />
              </label>
              <label>
                <span className={label}>Slóð fyrir viðskiptavini</span>
                <input
                  value={laufid.appUrl}
                  onChange={(e) =>
                    setLaufid({ ...laufid, appUrl: e.target.value })
                  }
                  className={input}
                  placeholder="https://app.laufid.is"
                />
              </label>
              <label>
                <span className={label}>Slug samstarfsaðila</span>
                <input
                  value={laufid.partnerSlug}
                  onChange={(e) =>
                    setLaufid({ ...laufid, partnerSlug: e.target.value })
                  }
                  className={input}
                  placeholder="atomika"
                />
              </label>
              <label>
                <span className={label}>
                  Partner-lykill {laufid.tokenSet && "(tómt = óbreytt)"}
                </span>
                <input
                  type="password"
                  value={laufid.token}
                  onChange={(e) =>
                    setLaufid({ ...laufid, token: e.target.value })
                  }
                  className={input}
                  placeholder={laufid.tokenSet ? "••••••••" : ""}
                />
              </label>
            </div>
          </section>

          <ApiKeysSection />

          {error && <p className="text-sm text-danger">{error}</p>}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="rounded-lg bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              {saving ? "Vista…" : "Vista stillingar"}
            </button>
            {saved && (
              <span className="text-[13px] font-semibold text-ok">
                Vistað ✓
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const SCOPE_HINTS: Record<ApiKeyScope, string> = {
  "customer-read": "Laufið — lesa gögn viðskiptavina",
  ingest: "Mælar — senda inn mælingar",
  read: "Lesa almenn gögn",
  admin: "Full stjórnendaréttindi",
};

const SCOPES = Object.keys(SCOPE_HINTS) as ApiKeyScope[];

function dt(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString("is-IS") : "–";
}

function ApiKeysSection() {
  const [keys, setKeys] = useState<ApiKeySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("Laufið");
  const [ownerOrg, setOwnerOrg] = useState("laufid");
  const [scopes, setScopes] = useState<Set<ApiKeyScope>>(
    new Set<ApiKeyScope>(["customer-read"]),
  );
  const [minting, setMinting] = useState(false);
  const [minted, setMinted] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .listApiKeys()
      .then(setKeys)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const toggleScope = (s: ApiKeyScope) => {
    const next = new Set(scopes);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setScopes(next);
  };

  const mint = () => {
    if (name.trim() === "") {
      setErr("Heiti vantar.");
      return;
    }
    if (scopes.size === 0) {
      setErr("Veldu að minnsta kosti eitt scope.");
      return;
    }
    setMinting(true);
    setErr(null);
    setCopied(false);
    api
      .createApiKey({
        name: name.trim(),
        ownerOrg,
        scopes: [...scopes].join(","),
      })
      .then((k) => {
        setMinted(k);
        load();
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setMinting(false));
  };

  const copy = () => {
    if (!minted) return;
    void navigator.clipboard.writeText(minted.key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const revoke = (k: ApiKeySummary) => {
    if (
      !window.confirm(
        `Afturkalla lykilinn „${k.name}“ (${k.prefix})?\n\nHann hættir samstundis að virka og það er ekki afturkræft.`,
      )
    ) {
      return;
    }
    api
      .revokeApiKey(k.id)
      .then(load)
      .catch((e: unknown) =>
        setErr(e instanceof Error ? e.message : String(e)),
      );
  };

  const remove = (k: ApiKeySummary) => {
    if (!window.confirm(`Eyða lyklinum „${k.name}“ (${k.prefix}) varanlega?`)) {
      return;
    }
    api
      .deleteApiKey(k.id)
      .then(load)
      .catch((e: unknown) =>
        setErr(e instanceof Error ? e.message : String(e)),
      );
  };

  return (
    <section className={card}>
      <h2 className="text-base font-bold text-ink">API-lyklar</h2>
      <p className="mt-1 text-[12.5px] text-muted-2">
        Bearer-lyklar sem utanaðkomandi kerfi nota til að lesa úr Atomika. Til
        að tengja Laufið: búðu til lykil með <strong>customer-read</strong>,
        afritaðu hann og límdu í Laufið undir Stillingar → Tengingar → „Atomika
        Customer API lykill“, ásamt grunnslóð þessa umhverfis. Lykillinn birtist
        aðeins einu sinni — hann er geymdur hashaður og verður ekki lesinn
        aftur.
      </p>

      {minted && (
        <div className="mt-4 rounded-xl border border-ok bg-ok-50 p-3">
          <div className="text-[12.5px] font-bold text-ok">
            Nýr lykill „{minted.name}“ — afritaðu hann núna
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded-lg border border-line bg-white px-3 py-2 font-mono text-[12.5px] text-ink">
              {minted.key}
            </code>
            <button
              type="button"
              onClick={copy}
              className="shrink-0 rounded-lg bg-ink px-3 py-2 text-[12.5px] font-bold text-white"
            >
              {copied ? "Afritað ✓" : "Afrita"}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setMinted(null)}
            className="mt-2 text-[12px] font-semibold text-muted-2 underline"
          >
            Ég er búinn að vista hann
          </button>
        </div>
      )}

      <div className="mt-4 rounded-xl border border-line bg-canvas p-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-4">
          Nýr lykill
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label>
            <span className={label}>Heiti</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={input}
              placeholder="Laufið"
            />
          </label>
          <label>
            <span className={label}>Eigandi</span>
            <select
              value={ownerOrg}
              onChange={(e) => setOwnerOrg(e.target.value)}
              className={input}
            >
              <option value="laufid">laufid</option>
              <option value="atomika">atomika</option>
            </select>
          </label>
        </div>
        <div className="mt-3">
          <span className={label}>Scopes</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {SCOPES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleScope(s)}
                title={SCOPE_HINTS[s]}
                className={`rounded-full px-3 py-1 text-[12px] font-bold transition-colors ${
                  scopes.has(s)
                    ? "bg-ink text-white"
                    : "border border-line bg-white text-muted-2 hover:text-ink"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <p className="mt-2 text-[12px] text-muted-3">
            {[...scopes].map((s) => SCOPE_HINTS[s]).join(" · ") ||
              "Ekkert valið"}
          </p>
        </div>
        <button
          type="button"
          onClick={mint}
          disabled={minting}
          className="mt-3 rounded-lg bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
        >
          {minting ? "Bý til…" : "Búa til lykil"}
        </button>
      </div>

      {err && <p className="mt-3 text-sm text-danger">{err}</p>}

      <div className="mt-4 divide-y divide-line">
        {loading ? (
          <p className="py-3 text-sm text-muted-3">Hleð…</p>
        ) : keys.length === 0 ? (
          <p className="py-3 text-sm text-muted-3">Engir lyklar til.</p>
        ) : (
          keys.map((k) => (
            <div
              key={k.id}
              className="flex flex-wrap items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-bold text-ink">
                    {k.name}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      k.active ? "bg-ok-50 text-ok" : "bg-danger-50 text-danger"
                    }`}
                  >
                    {k.active
                      ? "virkur"
                      : k.revokedAt
                        ? "afturkallaður"
                        : "útrunninn"}
                  </span>
                </div>
                <div className="mt-0.5 font-mono text-[12px] text-muted-2">
                  {k.prefix}… · {k.ownerOrg} · {k.scopes}
                </div>
                <div className="mt-0.5 text-[12px] text-muted-3">
                  Stofnaður {dt(k.createdAt)} · síðast notaður{" "}
                  {dt(k.lastUsedAt)}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                {k.active && (
                  <button
                    type="button"
                    onClick={() => revoke(k)}
                    className="rounded-lg border border-line bg-white px-3 py-1.5 text-[12.5px] font-bold text-muted hover:bg-canvas"
                  >
                    Afturkalla
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(k)}
                  className="rounded-lg border border-danger-50 bg-danger-50 px-3 py-1.5 text-[12.5px] font-bold text-danger hover:opacity-80"
                >
                  Eyða
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
